import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { indexMappings } from "../lib/ioMapping";
import { useSiteContext } from "../components/layout/Layout";
import { ComplianceTable } from "../components/acms/Shared";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Gauge, TrendingUp } from "lucide-react";
import { HydrantIcon, PumpIcon, ValveIcon } from "../components/acms/EquipmentIcons";
import { EquipmentCard as Card, Stat } from "../components/acms/EquipmentCard";

// Fallback full-scale range when a sensor's meta.range_bar isn't configured yet. Must be a real
// full-scale value, never an alarm setpoint — using an alarm-high value here would make the gauge
// read fuller than reality and misfire the LOW/red state early (pressure_gauge_scaling_bug.md).
const DEFAULT_MAX_PRESSURE_BAR = 40;

// A 4-20 mA current-loop transmitter never legitimately reports outside this range.
const SENSOR_MIN = 4;
const SENSOR_MAX = 20;

// This page was displaying the raw register value (raw mA) straight as "bar" everywhere — the
// mA -> bar conversion that Live Monitor applies was missing here entirely. Full-scale prefers the
// gateway IO Mapping's engineering_max (the figure actually calibrated against the physical
// transmitter) over the Asset Registry's own meta.range_bar, same precedence as Live Monitor.
function sensorMaxPressure(sensor, ioMap) {
  const ioMapping = sensor.gateway_id && sensor.io_point_name ? ioMap[`${sensor.gateway_id}:${sensor.io_point_name}`] : null;
  return ioMapping?.engineering_max != null
    ? Number(ioMapping.engineering_max)
    : (Number(sensor.meta?.range_bar) || DEFAULT_MAX_PRESSURE_BAR);
}

function pressureBar(sensor, rawValue, ioMap) {
  if (rawValue == null) return null;
  const raw = Number(rawValue);
  const maxPressure = sensorMaxPressure(sensor, ioMap);
  return Math.min(maxPressure, Math.max(0, ((raw - SENSOR_MIN) / (SENSOR_MAX - SENSOR_MIN)) * maxPressure));
}

// Pump Trip / Auto-Manual are independent signals from the pump's primary run/stop register
// (which already covers Pump Run Detection) — only rendered if linked, mirrors LiftsModule.jsx's
// conditional extra-register badge grid.
function PumpDetails({ pump, live }) {
  const trip = live?.extra?.pump_trip;
  const autoManual = live?.extra?.auto_manual;
  return (
    <div className="grid grid-cols-2 gap-1 mt-2">
      <Stat label="Make/Model">{pump.make || "—"}{pump.model ? ` · ${pump.model}` : ""}</Stat>
      <Stat label="Capacity">{pump.meta?.capacity_lpm || "—"} LPM</Stat>
      <Stat label="Power">{pump.meta?.kw || "—"} kW · {pump.meta?.power_source || "—"}</Stat>
      <Stat label="Run hours">{(pump.meta?.run_hours_total || 0).toFixed(1)}</Stat>
      {trip && (
        <Stat label="Trip" className={Number(trip.value) === 1 ? "text-red-600" : "text-emerald-600"}>{Number(trip.value) === 1 ? "TRIPPED" : "Normal"}</Stat>
      )}
      {autoManual && <Stat label="Auto/Manual">{Number(autoManual.value) === 1 ? "Auto" : "Manual"}</Stat>}
    </div>
  );
}

function TrendChart({ selectedSite, sensor, ioMap }) {
  const [hist, setHist] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!selectedSite || !sensor) return;
    let cancelled = false;
    const load = () => {
      api.get(`/v1/sites/${selectedSite}/telemetry/parameter-history`, { params: { parameter: sensor.io_point_name, hours: 24 } })
        .then((r) => {
          if (cancelled) return;
          // parameter-history returns the raw register value (raw mA), not bar — convert the
          // same way as the live reading, or the trend line plots mA on a bar-scaled chart.
          setHist((r.data || []).map((d) => ({
            ...d,
            value: pressureBar(sensor, d.value, ioMap),
            time: new Date(d.ingested_at || d.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
          })));
          setLoaded(true);
        }).catch(() => { if (!cancelled) setLoaded(true); });
    };
    load();
    const i = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(i); };
  }, [selectedSite, sensor, ioMap]);

  if (!loaded) return <div className="h-[320px] flex items-center justify-center text-xs text-gray-400">Loading…</div>;
  if (!hist.length) return <div className="h-[320px] flex items-center justify-center text-xs text-gray-400">Accumulating history…</div>;
  const alarmLow = sensor.meta?.alarm_low_bar || 3.5;
  return (
    <ResponsiveContainer width="100%" height={320}>
      <LineChart data={hist} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
        <CartesianGrid stroke="rgba(0,0,0,0.05)" strokeDasharray="3 3" />
        <XAxis dataKey="time" stroke="#94a3b8" fontSize={10} minTickGap={40} />
        <YAxis domain={[0, sensorMaxPressure(sensor, ioMap)]} stroke="#94a3b8" fontSize={10} width={36} />
        <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", color: "#1f2937" }} formatter={(v) => [`${Number(v).toFixed(2)} bar`, ""]} />
        <ReferenceLine y={alarmLow} stroke="#ff3366" strokeDasharray="5 5" label={{ value: `GR ${alarmLow} Bar`, position: "right", fill: "#ff3366", fontSize: 10 }} />
        <Line type="monotone" dataKey="value" stroke="#ff6b35" strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default function HydrantPage() {
  const { selectedSite, sites } = useSiteContext();
  const [pumps, setPumps] = useState([]);
  const [siteCfg, setSiteCfg] = useState(null);
  const [pressureSensors, setPressureSensors] = useState([]);
  const [hydrantValves, setHydrantValves] = useState([]);
  const [boosterValves, setBoosterValves] = useState([]);
  const [liveValues, setLiveValues] = useState({});
  const [gwStatusMap, setGwStatusMap] = useState({});
  const [ioMap, setIoMap] = useState({});
  const [trendSensor, setTrendSensor] = useState(null);

  useEffect(() => {
    if (!selectedSite) return;
    const load = () => {
      api.get(`/assets`, { params: { site_id: selectedSite, asset_type: "pump" } }).then((r) => setPumps(r.data || [])).catch(() => {});
      api.get(`/assets`, { params: { asset_type: "pressure_sensor", site_id: selectedSite, system: "hydrant" } }).then((r) => setPressureSensors(r.data || [])).catch(() => {});
      api.get(`/assets`, { params: { asset_type: "valve", site_id: selectedSite, system: "hydrant" } }).then((r) => setHydrantValves(r.data || [])).catch(() => {});
      api.get(`/assets`, { params: { asset_type: "valve", site_id: selectedSite, system: "booster" } }).then((r) => setBoosterValves(r.data || [])).catch(() => {});
      api.get(`/assets/live-values`, { params: { site_id: selectedSite } }).then((r) => setLiveValues(r.data || {})).catch(() => {});
    };
    load();
    // Site config (zone split, GR min setpoint) is configuration, not telemetry — fetched once
    // per site instead of on every 5s poll.
    api.get(`/site-config-full/${selectedSite}`).then((r) => setSiteCfg(r.data)).catch(() => {});
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [selectedSite]);

  // IO Mappings give the sensor's real calibrated full-scale range (engineering_max) — same
  // source Live Monitor's gauge uses. Keyed on the gateway id set, not the sensor array (which is
  // replaced on every 5s poll and would otherwise refetch mappings, and reload an open trend
  // chart, every 5s).
  const sensorGwKey = [...new Set(pressureSensors.map((s) => s.gateway_id).filter(Boolean))].sort().join(",");
  useEffect(() => {
    const gwIds = sensorGwKey ? sensorGwKey.split(",") : [];
    if (!gwIds.length) { setIoMap({}); return; }
    Promise.all(gwIds.map((gwId) => api.get(`/hardware/gateways/${gwId}/io-mappings`).then((r) => r.data || []).catch(() => [])))
      .then((lists) => {
        const map = {};
        Object.assign(map, indexMappings(lists.flat()));
        setIoMap(map);
      });
  }, [sensorGwKey]);

  // Gateway status for all gateways used by pumps/valves — same stable-key treatment.
  const equipGwKey = [...new Set([...pumps, ...hydrantValves, ...boosterValves].map((a) => a.gateway_id).filter(Boolean))].sort().join(",");
  useEffect(() => {
    const uniqueGateways = equipGwKey ? equipGwKey.split(",") : [];
    if (!uniqueGateways.length) return;
    const loadGatewayStatus = () => {
      uniqueGateways.forEach((gwId) => {
        api.get(`/v1/gateways/${gwId}/status`)
          .then((r) => setGwStatusMap((prev) => ({ ...prev, [gwId]: r.data })))
          .catch(() => {});
      });
    };
    loadGatewayStatus();
    const i = setInterval(loadGatewayStatus, 5000);
    return () => clearInterval(i);
  }, [equipGwKey]);

  const site = sites.find((s) => s.site_id === selectedSite);

  const hydrantPumps = pumps.filter((p) => p.system === "hydrant");
  // Matches both the legacy meta.pump_type field (pre-System-tag pumps) and the System: Booster
  // tag, so neither an old nor a newly-tagged booster pump goes missing.
  const boosters = pumps.filter((p) => p.meta?.pump_type === "booster" || p.system === "booster");
  const nothingRegistered = !hydrantPumps.length && !boosters.length && !hydrantValves.length && !boosterValves.length && !pressureSensors.length;

  const isSplit = !!siteCfg?.modules?.high_low_zone && (siteCfg?.zone_split_floor || 0) > 0;
  const grMin = siteCfg?.setpoints?.gr_min_bar || 3.5;
  const zoneLabel = (s) => {
    const zone = (s.meta || {}).zone;
    if (isSplit && zone === "high_zone") return "High Zone Pressure";
    if (isSplit && zone === "low_zone") return "Low Zone Pressure";
    return "Hydrant Pressure";
  };
  // High zone first, then low zone, then any other hydrant sensors — same order as before.
  const zoneRank = (s) => (!isSplit ? 2 : (s.meta || {}).zone === "high_zone" ? 0 : (s.meta || {}).zone === "low_zone" ? 1 : 2);
  const sortedSensors = [...pressureSensors].sort((a, b) => zoneRank(a) - zoneRank(b));

  const gwConnectedFor = (a) => (a.gateway_id ? gwStatusMap[a.gateway_id]?.connected ?? true : true);

  const renderPump = (p, group, fallbackLocation) => {
    const live = liveValues[p.asset_id];
    const gwConnected = gwConnectedFor(p);
    // If gateway is offline, show grayed out; otherwise show actual status
    const displayStatus = !gwConnected
      ? "offline"
      : live ? (Number(live.value) === 1 ? "running" : "stopped") : (p.meta?.legacy_status || p.status);
    const tripped = Number(live?.extra?.pump_trip?.value) === 1;
    const tone = !gwConnected ? "offline" : displayStatus === "fault" || displayStatus === "stopped" || tripped ? "bad" : displayStatus === "running" ? "good" : "idle";
    return (
      <Card
        key={p.asset_id}
        title={p.name}
        group={group}
        icon={PumpIcon}
        tone={tone}
        pulse={!!live && gwConnected && displayStatus === "running"}
        live={gwConnected ? live : undefined}
        testid={`${group.startsWith("Booster") ? "booster" : "hydrant-pump"}-${p.asset_id}`}
        footer={<PumpDetails pump={p} live={live} />}
      >
        <div className="text-lg font-extrabold uppercase tracking-wider leading-tight">{displayStatus}</div>
        <div className="text-[10px] text-gray-500 truncate">
          {p.location_label || fallbackLocation}
          {" · "}
          <span className={gwConnected && live ? "text-emerald-500" : "text-gray-400"}>{!gwConnected ? "gateway offline" : live ? "live" : p.gateway_id ? "awaiting data" : "not linked"}</span>
        </div>
      </Card>
    );
  };

  const renderValve = (v, group, fallbackLocation) => {
    const live = liveValues[v.asset_id];
    const gwConnected = gwConnectedFor(v);
    const open = !gwConnected ? null : (live ? Number(live.value) === 1 : null);
    const statusText = !gwConnected ? "Offline" : (open === null ? (v.gateway_id ? "Awaiting data" : "Not linked") : open ? "Open" : "Closed");
    const tone = !gwConnected ? "offline" : open ? "good" : "idle";
    return (
      <Card
        key={v.asset_id}
        title={v.name}
        group={group}
        icon={ValveIcon}
        tone={tone}
        live={gwConnected ? live : undefined}
        testid={`${group.startsWith("Booster") ? "booster-valve" : "hydrant-valve"}-${v.asset_id}`}
      >
        <div className="text-lg font-extrabold uppercase tracking-wider leading-tight truncate">{statusText}</div>
        <div className="text-[10px] text-gray-500 truncate">{v.location_label || fallbackLocation} · {v.meta?.type || "—"}</div>
      </Card>
    );
  };

  const complianceRows = [
    ...pressureSensors.map((s) => {
      const live = liveValues[s.asset_id];
      const val = live ? pressureBar(s, live.value, ioMap) : null;
      const min = siteCfg?.setpoints?.gr_min_bar || s.meta?.alarm_low_bar || 3.5;
      return {
        parameter: s.name,
        current: val != null ? `${val.toFixed(2)} Bar` : "—",
        required: `≥ ${min} Bar`,
        ok: val != null ? val >= min : false,
      };
    }),
    ...hydrantPumps.map((p) => {
      const live = liveValues[p.asset_id];
      const running = live ? Number(live.value) === 1 : null;
      const displayStatus = live ? (running ? "Running" : "Stopped") : (p.meta?.legacy_status || p.status);
      return {
        parameter: p.name,
        current: displayStatus,
        required: "Operational",
        ok: live ? true : p.status !== "fault",
      };
    }),
  ];

  return (
    <div className="space-y-3" data-testid="hydrant-page">
      {/* Header — one compact row */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 min-w-0 flex-wrap">
          <div className="w-9 h-9 rounded-lg bg-red-50 border border-red-200 text-red-600 flex items-center justify-center shrink-0">
            <HydrantIcon className="w-6 h-6" strokeWidth={1.75} />
          </div>
          <h1 className="text-xl md:text-2xl font-extrabold tracking-tight">Hydrant System</h1>
          <span className="text-[11px] text-gray-500 truncate">
            Dashboard → {site?.name} · GR Appendix b · auto-refresh 5s
          </span>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <a href="/pumps" className="text-orange-600 hover:underline">Pump Registry →</a>
          <a href="/registry/valves" className="text-orange-600 hover:underline">Valve Registry →</a>
        </div>
      </div>

      {nothingRegistered ? (
        <div className="glass rounded-lg p-8 text-center text-gray-500 text-sm">
          No hydrant pumps, pressure sensors, or valves registered yet. <a href="/pumps" className="text-orange-600 hover:text-orange-700 font-semibold">Add them in the Asset Registry</a>
        </div>
      ) : (
        // Pressure, pumps, boosters and valves share one dense grid (instead of a full-width
        // section per group) so the whole system fits on screen; each card's small orange label
        // names its group, and the icon badge's color shows its state.
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))" }}>
          {/* PRESSURE SENSORS — driven by site config (high/low zone split) + Asset Registry.
              No telemetry-simulator fallback values. */}
          {sortedSensors.map((sensor) => {
            const live = liveValues[sensor.asset_id];
            const val = live ? pressureBar(sensor, live.value, ioMap) : null;
            const tone = val == null ? "idle" : val >= 4 ? "good" : val >= grMin ? "warn" : "bad";
            const valueColor = val == null ? "" : val >= 4 ? "text-emerald-600" : val >= grMin ? "text-amber-600" : "text-red-600";
            const hasTrend = sensor.gateway_id && sensor.io_point_name;
            return (
              <Card
                key={sensor.asset_id}
                title={sensor.name}
                group={zoneLabel(sensor)}
                icon={Gauge}
                tone={tone}
                live={live}
                testid={`zone-${sensor.meta?.zone || sensor.asset_id}`}
                footer={
                  <>
                    <div className="grid grid-cols-2 gap-1 mt-2">
                      <Stat label="GR Min">≥ {grMin} BAR</Stat>
                      <Stat label="Alarm Setpoints">L {sensor.meta?.alarm_low_bar ?? "—"} · H {sensor.meta?.alarm_high_bar ?? "—"}</Stat>
                    </div>
                    {hasTrend && (
                      <button
                        type="button"
                        onClick={() => setTrendSensor(sensor)}
                        className="mt-2 self-center inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-gray-200 text-[10px] font-semibold uppercase tracking-wider text-gray-500 hover:text-orange-600 hover:border-orange-300 hover:bg-orange-50 transition-colors"
                      >
                        <TrendingUp className="w-3 h-3" strokeWidth={2.5} /> 24h trend
                      </button>
                    )}
                  </>
                }
              >
                {val != null ? (
                  <div className="flex items-baseline gap-2">
                    <span className={`text-2xl font-extrabold tabular-nums leading-tight ${valueColor}`}>{val.toFixed(2)}</span>
                    <span className="text-xs text-gray-500 font-semibold">BAR</span>
                    <span className={`ml-auto text-[10px] font-bold px-1.5 py-px rounded-full border ${val >= grMin ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}>{val >= grMin ? "PASS" : "FAIL"}</span>
                  </div>
                ) : (
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider font-bold py-1">
                    {sensor.gateway_id ? "Awaiting data" : "Not wired to live data"}
                  </div>
                )}
                <div className="text-[10px] text-gray-500 truncate">{sensor.location_label || "—"}</div>
              </Card>
            );
          })}

          {/* HYDRANT PUMPS — any pump tagged System: Hydrant, regardless of pump_type */}
          {hydrantPumps.map((p) => renderPump(p, "Hydrant Pump", "—"))}

          {/* BOOSTER PUMPS (TERRACE) */}
          {boosters.map((b) => renderPump(b, "Booster Pump · Terrace", "Terrace"))}

          {/* HYDRANT VALVES — any valve tagged System: Hydrant */}
          {hydrantValves.map((v) => renderValve(v, "Hydrant Valve", "—"))}

          {/* BOOSTER VALVES — any valve tagged System: Booster */}
          {boosterValves.map((v) => renderValve(v, "Booster Valve · Terrace", "Terrace"))}
        </div>
      )}

      {/* 24h trend — per sensor, in a modal so it doesn't push the cards off screen */}
      <Dialog open={!!trendSensor} onOpenChange={(open) => { if (!open) setTrendSensor(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{trendSensor?.name || "Hydrant Riser Pressure"} — Last 24 Hours</DialogTitle>
          </DialogHeader>
          {trendSensor && <TrendChart selectedSite={selectedSite} sensor={trendSensor} ioMap={ioMap} />}
        </DialogContent>
      </Dialog>

      {/* GR compliance — mandatory, so always shown inline below the cards (not behind a modal) */}
      {(hydrantPumps.length > 0 || pressureSensors.length > 0) && (
        <ComplianceTable title="Section A — Hydrant System (GR Appendix b)" rows={complianceRows} />
      )}
    </div>
  );
}
